import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, Users, Trophy, Zap } from 'lucide-react';

const AboutClan = () => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const features = [
    {
      icon: Target,
      title: 'Missão',
      description: 'Formar a comunidade de jogadores mais unida e competitiva, onde cada membro contribui para o sucesso coletivo.'
    },
    {
      icon: Users,
      title: 'Comunidade',
      description: 'Ambiente acolhedor e profissional onde jogadores de todos os níveis são bem-vindos e incentivados a evoluir.'
    },
    {
      icon: Trophy,
      title: 'Conquistas',
      description: 'Conquistas que refletem o esforço e a dedicação de cada membro.'
    },
    {
      icon: Zap,
      title: 'Evolução',
      description: 'Eventos constantes, desafios épicos e um sistema de progressão que mantém a motivação sempre em alta.'
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100 rounded-full filter blur-3xl opacity-20"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
            Sobre o <span className="bg-gradient-to-r from-blue-600 to-cyan-600 text-transparent bg-clip-text">Revolution</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Mais do que um clã, somos uma família de guerreiros dedicados à excelência
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="group relative"
            >
              {/* Hover glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/10 group-hover:to-cyan-500/10 rounded-3xl transition-all duration-300"></div>
              
              {/* Card */}
              <div className="relative bg-white/80 backdrop-blur-sm border border-gray-200/50 p-8 rounded-3xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                {/* Icon container */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                  <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Bottom corner decoration */}
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-blue-200 rounded-br-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-3xl p-12 md:p-16 text-white text-center shadow-2xl"
        >
          {/* Animated grid background */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20"></div>
          
          {/* Glow effects */}
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-500/20 rounded-full filter blur-3xl"></div>
          
          <div className="relative z-10">            
            <h3 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-white via-blue-200 to-cyan-200 text-transparent bg-clip-text">
              Nossa História
            </h3>
            <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Fundado por jogadores apaixonados, o Revolution nasceu com o objetivo de criar 
              uma comunidade onde a <span className="text-cyan-400 font-semibold">cooperação</span> e a <span className="text-blue-400 font-semibold">competição saudável</span> andam lado a lado. 
              Hoje, somos referência em organização, eventos e desenvolvimento de jogadores.
            </p>
            
            {/* Decorative elements */}
            <div className="mt-8 flex justify-center gap-4">
              <div className="w-16 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full"></div>
              <div className="w-16 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent rounded-full"></div>
              <div className="w-16 h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent rounded-full"></div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutClan;
